import { AuthorAvatar } from '@components/ArtBoard/Threads/components/AuthorAvatar'
import { Tooltip } from '@lib/components'
import { formatDate, timeAgo } from '@lib/utils/date'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { CommentsActions } from '@store/reducers/comments'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { OpenConfirmation } from '@/store'
import type { Comment as CommentType } from '@/types/models'
import { clsJoin } from '@/utils'
import type { PayloadWithoutId } from '../../types'
import css from './Comment.module.scss'
import { Actions } from './components/Actions'
import { Content } from './components/Content'
import { useSetReaction } from './hooks'

type Props = {
  last: boolean
  onCloseHandle: () => void
  onCommit: (payload: PayloadWithoutId) => void
  onEditHandle: () => void
}

const Comment: React.FC<Props & CommentType> = ({
  id,
  author,
  content,
  createdAt,
  last,
  onCloseHandle,
  onCommit,
  onEditHandle,
  owner,
}) => {
  const dispatch = useDispatch()

  const [isEditing, setIsEditing] = useState(false)

  const user = useSelector(
    migrationProjectSelector.getProjectRecipientById(owner)
  )

  const setReaction = useSetReaction()

  useEffect(() => {
    if (isEditing) {
      return onEditHandle()
    }

    return onCloseHandle()
  }, [isEditing])

  const handleCancel = () => {
    onCloseHandle()

    setIsEditing(false)
  }

  const handleDelete = () => {
    dispatch(
      OpenConfirmation({
        label: 'Delete',
        title: 'Delete comment',
        message: 'This comment will be permanently deleted. Are you sure?',
        then: [CommentsActions.delete({ id })],
      })
    )
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCommit = async (payload: PayloadWithoutId) => {
    await onCommit(payload)

    setIsEditing(false)
  }

  const handleSelectEmoji = (emoji: string) => {
    setReaction({
      commentId: id,
      emoji,
    })
  }

  return (
    <div className={clsJoin(css.container, last && css['last-container'])}>
      <div className={css.avatar}>
        <AuthorAvatar size="md" author={user} />
      </div>

      <div className={css.body}>
        <div className={css.heading}>
          <div className={css.author}>{author || 'Guest'}</div>

          <div className={css.information}>
            <Tooltip content={formatDate(createdAt)} position="top">
              <div className={css.since}>{timeAgo(createdAt)}</div>
            </Tooltip>

            <Actions
              onClickDelete={handleDelete}
              onClickEdit={handleEdit}
              onSelectEmoji={handleSelectEmoji}
              owner={owner}
            />
          </div>
        </div>

        <div className={css.content}>
          <Content
            isEditing={isEditing}
            onCancel={handleCancel}
            onCommit={handleCommit}
            value={content}
          />
        </div>

        {/* <Reactions id={id} onClick={handleSelectEmoji} items={reactions} /> */}

        {!last && <div className={css.divider} />}
      </div>
    </div>
  )
}

export default Comment
